import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../assets/css/ProbeList.scss';
import "../../config";

// Lazy Image Component
const LazyImage = ({ src, alt, className, onLoad, placeholder, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    setHasError(true);
  };

  return (
    <div ref={imgRef} className={`lazy-image-container ${className || ''}`} {...props}>
      {isInView && !hasError && (
        <img
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`lazy-image ${isLoaded ? 'loaded' : 'loading'}`}
          style={{ display: isLoaded ? 'block' : 'none' }}
        />
      )}
      {(!isInView || !isLoaded || hasError) && (
        <div className="image-placeholder">
          {hasError ? (
            <i className="fa fa-exclamation-triangle"></i>
          ) : placeholder ? (
            placeholder
          ) : (
            <i className="fa fa-image"></i>
          )}
        </div>
      )}
    </div>
  );
};

const ProbeList = ({
  historyData,
  arrayProbeImages,
  relatedAccounts = [],
  onViewResults,
  formatDate
}) => {
  const navigate = useNavigate();

  // Find probe image by Image_ID
  const findProbeImageById = (imageId) => {
    return arrayProbeImages.find(img =>
      img.Image_ID === imageId ||
      img.Image_ID === parseInt(imageId) ||
      img.ID === imageId ||
      img.ID === parseInt(imageId)
    );
  };

  const findProbeOwner = (record) => {
    if (!relatedAccounts || relatedAccounts.length === 0) return 'N/A';
    const owner = relatedAccounts.find(acc => acc.User_IDValue === record.User_ID && acc.User_ID_ExtValue === record.User_ID_Ext);
    return owner ? owner.Name || 'N/A' : 'N/A';
  };

  // Handle view results click
  const handleViewResults = (recordId) => {
    if (onViewResults) {
      onViewResults(recordId);
    } else {
      navigate(`/analyze/${recordId}`);
    }
  };

  return (
    <div className="probe-list">
      <div className="probe-list-header">
        <div className="header-left">
          <span className="list-title">Analysis History</span>
          <span className="list-count">{historyData.length} records</span>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary">
            <i className="fa fa-download"></i>
            Export
          </button>
        </div>
      </div>

      <div className="probe-list-content">
        {historyData.map((record, index) => {
          const probeImage = record.CustomFilters?.Image_ID ? findProbeImageById(record.CustomFilters.Image_ID) : null;

          return (
            <div
              key={record.ID || record.Id || index}
              className="probe-list-item"
            >
              <div className="item-header">
                <div className="item-number">{index + 1}</div>
                <div className="item-meta">
                  <div className="analyze-date">
                    <label>Analyze Date/Time</label>
                    <span>{formatDate(record.CreatedTicks || record.CreatedTimeValue || record.CreateDate || record.CreatedDate || record.Timestamp)}</span>
                  </div>
                  <div className="analyze-by">
                    <label>Analyze By</label>
                    <span>{findProbeOwner(record)}</span>
                  </div>
                </div>
              </div>

              <div className="item-content">
                <div className="probe-image-section">
                  <label>Probe Image</label>
                  <div className="probe-image-container">
                    {probeImage ? (
                      <LazyImage
                        src={
                          probeImage.PublicPath ||
                          probeImage.ImagePath ||
                          (probeImage.RelativePath && `${global.config.images3.link}/${probeImage.RelativePath}`) ||
                          `${global.config.images3.link}/${probeImage.RelativePath || probeImage.ImagePath}`
                        }
                        alt="Probe"
                        className="probe-image"
                        placeholder={<i className="fa fa-shoe-prints"></i>}
                      />
                    ) : record.ImageUrls && record.ImageUrls.length > 0 ? (
                      <LazyImage
                        src={record.ImageUrls[0]}
                        alt="Probe"
                        className="probe-image"
                        placeholder={<i className="fa fa-shoe-prints"></i>}
                      />
                    ) : (
                      <div className="no-image">
                        <i className="fa fa-image"></i>
                        <span>No Image</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="results-section">
                  <div className="results-actions">
                    <button
                      className="btn btn-primary view-results-btn"
                      onClick={() => handleViewResults(record.ID || record.Id)}
                      disabled={!record.ID && !record.Id}
                    >
                      <i className="fa fa-eye"></i>
                      View Results
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProbeList;
